"use client"

import { t } from "@/languages/i18n"
import { CameraOutlined, EditOutlined } from "@ant-design/icons"
import { Button, Form, Input, InputNumber, Modal } from "antd"
import dynamic from "next/dynamic"
import React, { useRef, useState } from "react"

import { removeAccents } from "@/lib/utils"
import { buildMediaHTML } from "@/utils/guessMimeFromUrl"
import { FieldType, MediaData, PostFormProps } from "../../../../lib/config/types"
import { slugPattern } from "../../../../utils/validationPatterns"
import AddNewMedia from "../../media/pages/AddNewMedia"
import Media from "../../media/pages/Media"
import { checkSlug } from "../services/post"

const CKEditorComponent = dynamic(() => import("../../../../lib/config/lib/Ckeditor"), { ssr: false })

const isValidJson = (str: string): boolean => {
  try {
    JSON.parse(str)
    return true
  } catch (e) {
    return false
  }
}

const PostForm: React.FC<PostFormProps> = ({ form, onFinishFailed, quillValue, onFinish }) => {
  const editorRef = useRef<any>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isChooseMedia, setIsChooseMedia] = useState(true)
  const [modalMetaImage, setModalMetaImage] = useState(false)
  const [isEditableSlug, setIsEditableSlug] = useState(true)

  const handleSubmit = () => {
    const formData = form.getFieldsValue()
    onFinish(formData)
  }

  const handleOpenModal = (modalMetaImage: boolean) => {
    setModalMetaImage(modalMetaImage)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleSelectMedia = (media: MediaData) => {
    const html = buildMediaHTML(media)
    const editor = editorRef.current

    if (editor) {
      // Insert at current cursor position using CKEditor's model API
      const viewFragment = editor.data.processor.toView(html)
      const modelFragment = editor.data.toModel(viewFragment)
      editor.model.insertContent(modelFragment)
    } else {
      // Fallback if editor not ready: append to end
      const current = form.getFieldValue("content") || ""
      form.setFieldsValue({ content: `${current}${html}` })
      setTimeout(() => form.validateFields(["content"]), 100)
    }

    setIsModalOpen(false)
  }

  const handleNameBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const nameValue = e.target.value.trim()
    if (!nameValue) {
      form.setFields([{ name: "slug", errors: [] }])
      return
    }

    const slugValue = removeAccents(nameValue)

    const isSlugUnique = async (slug: string): Promise<boolean> => {
      try {
        const response = await checkSlug(slug)
        return response.data.length === 0
      } catch {
        return false
      }
    }

    const isUnique = await isSlugUnique(slugValue)
    if (!isUnique) {
      form.setFieldsValue({ slug: slugValue })
      form.setFields([{ name: "slug", errors: [t("Slug already exists. Please choose another slug.")] }])
    } else {
      form.setFieldsValue({ slug: slugValue })
      form.setFields([{ name: "slug", errors: [] }])
    }
  }

  return (
    <Form
      form={form}
      className="flex flex-col w-[77%]"
      name="basic"
      initialValues={{ remember: true }}
      onFinish={handleSubmit}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
      layout="vertical"
    >
      <Form.Item<FieldType>
        name="name"
        rules={[{ required: true, message: t("Please enter article name!") }]}
        className="mt-4"
      >
        <Input placeholder={t("Add Name")} className="text-xl custom-input" onBlur={handleNameBlur} />
      </Form.Item>
      <div className="flex w-full">
        <Form.Item<FieldType>
          className="w-1/2 mr-4"
          name="slug"
          rules={[
            {
              pattern: slugPattern,
              message: 'Slug must match the following: "A-Z, a-z, 0-9, - ,_ ,. ,~"',
            },
          ]}
          label={<span className="text-2xl font-medium pb-2">{t("Slug")}</span>}
          extra={<span className="text-sm">{t("May not need to be entered (automatically render by name)")}</span>}
        >
          <Input disabled={isEditableSlug} placeholder={t("Slug")} className="custom-input" />
        </Form.Item>
        <Button
          className="self-center mb-2"
          icon={<EditOutlined />}
          color={!isEditableSlug ? "primary" : "default"}
          variant="outlined"
          onClick={() => setIsEditableSlug(!isEditableSlug)}
        >
          {t("Edit")}
        </Button>
      </div>
      <Form.Item>
        <Button icon={<CameraOutlined />} onClick={() => handleOpenModal(false)}>
          {t("Add media")}
        </Button>
      </Form.Item>

      <Form.Item<FieldType> name="content">
        <div
          style={{
            height: "500px",
            overflowY: "auto",
            position: "relative",
          }}
        >
          <CKEditorComponent
            value={form.getFieldValue("content")}
            onChange={(data: string) => {
              form.setFieldsValue({ content: data })
            }}
            onEditorReady={(editor: any) => {
              editorRef.current = editor
            }}
          />
        </div>
      </Form.Item>

      <Form.Item<FieldType> name="excerpt" label={<span className="text-base font-medium">{t("Excerpt")}</span>}>
        <Input className="custom-input" />
      </Form.Item>

      <Form.Item<FieldType>
        name="shortDescription"
        label={<span className="text-base font-medium">{t("Short Description")}</span>}
      >
        <Input.TextArea className="custom-input" rows={3} placeholder={t("Short Description Placeholder")} />
      </Form.Item>

      <Form.Item<FieldType>
        name="readingTime"
        label={<span className="text-base font-medium">{t("ReadingTime")}</span>}
      >
        <InputNumber className="custom-input" />
      </Form.Item>

      <span className="text-base font-medium pt-2 pb-2">{t("SEO")}</span>

      <div className="grid grid-cols-2 gap-x-5 gap-y-2 p-5 border rounded-[10px]">
        <Form.Item<FieldType>
          name="metaTitle"
          label={<span className="text-base font-medium p">{t("Meta Title")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>

        <Form.Item<FieldType>
          name="keywords"
          label={<span className="text-base font-medium">{t("Meta Keywords")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>

        <Form.Item<FieldType>
          name="metaDescription"
          label={<span className="text-base font-medium">{t("Meta Description")}</span>}
        >
          <Input.TextArea className="custom-input py-2" rows={5} />
        </Form.Item>

        <Form.Item<FieldType>
          name="structuredData"
          label={<span className="text-base font-medium">{t("Structured Data")}</span>}
          rules={[
            {
              validator: (_, value) => {
                if (value && !isValidJson(value)) {
                  return Promise.reject(new Error(t("Invalid JSON format!")))
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <Input.TextArea className="custom-input py-2" rows={5} placeholder={t('@example: {"id": "string"}')} />
        </Form.Item>

        <Form.Item<FieldType>
          name="metaRobots"
          label={<span className="text-base font-medium">{t("Meta Robots")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>

        <Form.Item<FieldType>
          name="metaViewport"
          label={<span className="text-base font-medium">{t("Meta Viewport")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>

        <Form.Item<FieldType>
          name="canonicalURL"
          label={<span className="text-base font-medium">{t("Canonical URL")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>
      </div>

      <Modal
        open={isModalOpen}
        title={<span className="ml-4">{t("Chọn ảnh")}</span>}
        onCancel={handleCloseModal}
        style={{ top: 20 }}
        width="95%"
        footer={null}
      >
        <div className="ml-4 mt-5">
          <Button
            onClick={() => setIsChooseMedia(true)}
            className="mr-2"
            style={{ backgroundColor: isChooseMedia ? "blue" : "initial", color: isChooseMedia ? "white" : "initial" }}
          >
            {t("Select Media")}
          </Button>
          <Button
            onClick={() => setIsChooseMedia(false)}
            style={{
              backgroundColor: !isChooseMedia ? "blue" : "initial",
              color: !isChooseMedia ? "white" : "initial",
            }}
          >
            {t("Upload Media")}
          </Button>
        </div>
        <div>
          {isChooseMedia ? (
            <Media
              isOpenModal={true}
              onSelectMedia={media => {
                modalMetaImage ? handleSelectMedia(media) : handleSelectMedia(media)
              }}
            />
          ) : (
            <AddNewMedia isOpenModal={true} setChooseMedia={setIsChooseMedia} />
          )}
        </div>
      </Modal>
    </Form>
  )
}

export default PostForm
